import React, { useEffect, useRef, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { OWNER_EMAIL, setSupplierCategories } from "@/lib/constants";
import { QrCode, RefreshCw } from "lucide-react";
import { useRefresh } from "@/lib/refresh";
import { LayoutDashboard, FileText, Users, Package, LogOut, UserCog, Eye, Lightbulb, Contact, ChevronDown, Layers, Truck, Trash2, Building2, ClipboardList, Wallet, Menu, X, Camera, Pencil, Ship, Target, Boxes, Wrench } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/opportunities", label: "Opportunities", icon: Target, testid: "nav-opportunities", mod: "opportunities" },
  { to: "/rfqs", label: "RFQs", icon: FileText, testid: "nav-rfqs", mod: "rfqs" },
  {
    group: "Orders", icon: ClipboardList, testid: "nav-orders", mod: "orders",
    children: [
      { to: "/orders", label: "Customer PO's", icon: FileText, testid: "nav-orders-customer" },
      { to: "/orders?section=somcore", label: "Somcore PO's", icon: Package, testid: "nav-orders-somcore" },
    ],
  },
  {
    group: "Logistics", icon: Truck, testid: "nav-logistics-group", mod: "logistics",
    children: [
      { to: "/logistics", label: "Freight Sourcing", icon: Ship, testid: "nav-freight" },
      { to: "/logistics?section=shipments", label: "Shipments", icon: Package, testid: "nav-shipments" },
    ],
  },
  {
    group: "Catalog", icon: Boxes, testid: "nav-catalog-group", mod: "catalog",
    children: [
      { to: "/catalog", label: "Product Catalog", icon: Package, testid: "nav-product-catalog" },
      { to: "/catalog/stock", label: "Stock Levels", icon: Boxes, testid: "nav-stock-levels" },
    ],
  },
  {
    group: "CRM", icon: Contact, testid: "nav-crm-group",
    children: [
      { to: "/industries", label: "Industries & Categories", icon: Layers, testid: "nav-industries" },
      { to: "/suppliers", label: "Suppliers", icon: Package, testid: "nav-suppliers" },
      { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
      { to: "/events", label: "Events & Visits", icon: QrCode, testid: "nav-events" },
    ],
  },
  { external: "/finance", label: "Finance Hub", icon: Wallet, testid: "nav-finance", mod: "finance" },
  { to: "/service", label: "Service Center", icon: Wrench, testid: "nav-service", mod: "service" },
  { to: "/settings", label: "Company Information", icon: Building2, testid: "nav-settings", mod: "company" },
  { to: "/team", label: "Team", icon: UserCog, testid: "nav-team", adminOnly: true },
  { to: "/ideas", label: "Ideas", icon: Lightbulb, testid: "nav-ideas", ownerOnly: true },
  { to: "/deleted-versions", label: "Recycle Bin", icon: Trash2, testid: "nav-deleted", adminOnly: true },
];

const linkClass = ({ isActive }) =>
  `sidebar-link flex items-center gap-3.5 rounded-lg px-3.5 py-3 text-[15px] font-medium ${
    isActive ? "bg-white text-brand-deep shadow-sm" : "text-slate-300 hover:bg-white/10 hover:text-white"
  }`;

function AvatarCropper({ src, onCancel, onApply }) {
  const VP = 224, OUT = 256;
  const [img, setImg] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  const base = img ? Math.max(VP / img.naturalWidth, VP / img.naturalHeight) : 1;
  const eff = base * zoom;
  const dw = img ? img.naturalWidth * eff : VP;
  const dh = img ? img.naturalHeight * eff : VP;
  const clamp = (p) => ({ x: Math.min(0, Math.max(VP - dw, p.x)), y: Math.min(0, Math.max(VP - dh, p.y)) });
  useEffect(() => {
    const im = new Image();
    im.onload = () => {
      setImg(im);
      const b = Math.max(VP / im.naturalWidth, VP / im.naturalHeight);
      setZoom(1);
      setPos({ x: (VP - im.naturalWidth * b) / 2, y: (VP - im.naturalHeight * b) / 2 });
    };
    im.src = src;
  }, [src]);
  useEffect(() => { if (img) setPos((p) => clamp(p)); /* eslint-disable-next-line */ }, [zoom, img]);
  const start = (e) => { const t = e.touches?.[0] || e; drag.current = { sx: t.clientX, sy: t.clientY, ox: pos.x, oy: pos.y }; };
  const move = (e) => {
    if (!drag.current) return;
    const t = e.touches?.[0] || e;
    setPos(clamp({ x: drag.current.ox + (t.clientX - drag.current.sx), y: drag.current.oy + (t.clientY - drag.current.sy) }));
  };
  const end = () => { drag.current = null; };
  const apply = () => {
    if (!img) return;
    const c = document.createElement("canvas"); c.width = OUT; c.height = OUT;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, OUT, OUT);
    const r = OUT / VP;
    ctx.drawImage(img, pos.x * r, pos.y * r, dw * r, dh * r);
    onApply(c.toDataURL("image/jpeg", 0.92));
  };
  return (
    <div className="flex flex-col items-center gap-3" data-testid="avatar-cropper">
      <div
        className="relative overflow-hidden rounded-full border-2 border-[#1B4383] bg-slate-100 touch-none"
        style={{ width: VP, height: VP, cursor: drag.current ? "grabbing" : "grab" }}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      >
        {img && <img src={src} alt="" draggable={false} className="absolute max-w-none select-none" style={{ left: pos.x, top: pos.y, width: dw, height: dh }} />}
      </div>
      <div className="flex w-full max-w-[224px] items-center gap-2">
        <span className="text-xs text-muted-foreground">Zoom</span>
        <input type="range" min="1" max="3" step="0.01" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 accent-[#1B4383]" data-testid="avatar-zoom" />
      </div>
      <p className="text-center text-[11px] text-muted-foreground">Drag to reposition · slide to resize. Saved as a square 256×256 photo.</p>
      <div className="flex gap-2">
        <Button type="button" size="sm" variant="outline" onClick={onCancel} data-testid="avatar-crop-cancel">Cancel</Button>
        <Button type="button" size="sm" onClick={apply} className="bg-[#1B4383] hover:bg-[#16386e]" data-testid="avatar-crop-apply">Apply Photo</Button>
      </div>
    </div>
  );
}

function ProfileDialog({ open, onOpenChange }) {
  const { user, refreshUser } = useAuth();
  const isAdmin = user?.role === "admin";
  const [f, setF] = useState({ name: "", full_name: "", phone: "", job_title: "", avatar: "" });
  const [saving, setSaving] = useState(false);
  const [cropSrc, setCropSrc] = useState("");
  useEffect(() => {
    if (open && user) { setF({ name: user.name || "", full_name: user.full_name || "", phone: user.phone || "", job_title: user.job_title || "", avatar: user.avatar || "" }); setCropSrc(""); }
  }, [open, user]);
  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Please use an image under 5MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setCropSrc(String(reader.result));
    reader.readAsDataURL(file);
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/auth/profile", f);
      await refreshUser();
      toast.success("Profile updated");
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="profile-dialog">
        <DialogHeader><DialogTitle>My Profile</DialogTitle></DialogHeader>
        {cropSrc ? (
          <AvatarCropper src={cropSrc} onCancel={() => setCropSrc("")} onApply={(d) => { setF((p) => ({ ...p, avatar: d })); setCropSrc(""); }} />
        ) : (
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-xl font-semibold text-slate-500">
            {f.avatar ? <img src={f.avatar} alt="" className="h-full w-full object-cover" /> : (f.name || user?.email || "?").charAt(0).toUpperCase()}
          </div>
          <div className="flex flex-col items-start gap-1.5">
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-input bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50" data-testid="profile-avatar-label">
              <Camera className="h-4 w-4" /> {f.avatar ? "Change Photo" : "Upload Photo"}
              <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="profile-avatar-input" />
            </label>
            {f.avatar && (
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => setCropSrc(f.avatar)} className="text-xs font-medium text-[#1B4383] hover:underline" data-testid="profile-avatar-adjust">Reposition / Resize</button>
                <button type="button" onClick={() => setF((p) => ({ ...p, avatar: "" }))} className="text-xs text-destructive hover:underline" data-testid="profile-avatar-remove">Remove</button>
              </div>
            )}
          </div>
        </div>
        )}
        <div className="mt-2 space-y-3">
          <div className="space-y-1.5"><Label>Display Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="profile-name" /></div>
          <div className="space-y-1.5"><Label>Full Name</Label><Input value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} data-testid="profile-fullname" /></div>
          {isAdmin ? (
            <div className="space-y-1.5"><Label>Job Title</Label><Input value={f.job_title} onChange={(e) => setF({ ...f, job_title: e.target.value })} placeholder="e.g. Managing Director" data-testid="profile-job-title" /></div>
          ) : (
            <div className="space-y-1.5"><Label>Job Title <span className="font-normal text-muted-foreground">(set by your admin)</span></Label><Input value={f.job_title || "—"} disabled data-testid="profile-job-title" /></div>
          )}
          <div className="space-y-1.5"><Label>Phone Number</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="e.g. +255 xxx xxx xxx" data-testid="profile-phone" /></div>
          <div className="space-y-1.5"><Label>Email <span className="font-normal text-muted-foreground">(cannot be changed)</span></Label><Input value={user?.email || ""} disabled data-testid="profile-email" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="profile-save" className="bg-[#1B4383] hover:bg-[#16386e]">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

let _catsLoaded = false;

export default function AppLayout({ children, title, actions, noRefresh = false }) {
  useEffect(() => {
    if (_catsLoaded) { return; }
    _catsLoaded = true;
    api.get("/supplier-categories").then((r) => setSupplierCategories(r.data.all)).catch(() => { _catsLoaded = false; });
  }, []);
  const { refresh } = useRefresh();
  const [spin, setSpin] = useState(false);
  const doRefresh = () => { setSpin(true); refresh(); toast.success("Refreshed — showing the latest saved details"); };
  const { user, logout, isImpersonating, returnToAdmin } = useAuth();
  const location = useLocation();
  const groupActive = (n) => (n.children || []).some((c) => location.pathname === c.to.split("?")[0]);
  const childActive = (c) => {
    const [path, query] = c.to.split("?");
    if (location.pathname !== path) return false;
    if (query) return location.search.includes(query);
    return !location.search.includes("section=");
  };
  const [openGroups, setOpenGroups] = useState({});
  const [pendingFU, setPendingFU] = useState(0);
  const [urgent, setUrgent] = useState(0);
  useEffect(() => {
    const load = () => {
      api.get("/event-follow-ups").then((r) => setPendingFU(r.data.pending || 0)).catch(() => {});
      api.get("/client-requests/alerts").then((r) => setUrgent(r.data.count || 0)).catch(() => {});
    };
    load();
    window.addEventListener("somcore:alerts", load);
    return () => window.removeEventListener("somcore:alerts", load);
  }, [location.pathname, location.search]);
  const badge = (k) => (k === "nav-events" || k === "nav-crm-group") && pendingFU > 0
    ? <span className="ml-auto rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white" data-testid={`${k}-pending-badge`}>{pendingFU}</span> : null;
  const [mobileNav, setMobileNav] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const canSeeMod = (mod) => {
    if (user?.role === "admin") return true;
    if (mod === "finance") return Array.isArray(user?.permissions) && user.permissions.includes("finance");
    return user?.permissions == null || !mod || user.permissions.includes(mod);
  };
  useEffect(() => {
    const active = NAV.find((n) => n.group && groupActive(n));
    if (active) setOpenGroups((o) => ({ ...o, [active.group]: true }));
    setMobileNav(false);
  }, [location.pathname]);
  const navItems = NAV.filter((n) =>
    (!n.adminOnly || user?.role === "admin") &&
    (!n.ownerOnly || (user?.email || "").toLowerCase() === OWNER_EMAIL) &&
    canSeeMod(n.mod)
  );
  useEffect(() => {
    document.title = title ? `${title} · Somcore Operations Hub` : "Somcore Operations Hub";
  }, [title]);
  return (
    <div className="flex min-h-screen bg-background">
      {mobileNav && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setMobileNav(false)} data-testid="sidebar-backdrop" />}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 transform bg-brand-deep text-steel-300 flex flex-col transition-transform duration-200 lg:translate-x-0 ${mobileNav ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="relative flex items-center justify-center px-6 h-16 border-b border-white/10">
          <Link to="/" onClick={() => setMobileNav(false)} aria-label="Go to dashboard" data-testid="sidebar-logo-link"><img src="/somcore-logo-white.png" alt="Somcore" className="h-9 w-auto" /></Link>
          <button type="button" onClick={() => setMobileNav(false)} className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-slate-300 hover:bg-white/10 lg:hidden" data-testid="sidebar-close-button" aria-label="Close menu">
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1.5 overflow-y-auto">
          <div className="px-3 pb-2 text-[11px] font-semibold tracking-wide text-white">Workspace</div>
          {navItems.map((n) => (
            n.group ? (
              <div key={n.group}>
                <button
                  type="button"
                  data-testid={n.testid}
                  onClick={() => setOpenGroups((o) => ({ ...o, [n.group]: !o[n.group] }))}
                  className={`sidebar-link flex w-full items-center gap-3.5 rounded-lg px-3.5 py-3 text-[15px] font-medium ${
                    groupActive(n)
                      ? "text-white bg-white/10" : "text-slate-300 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  <n.icon className="h-5 w-5 shrink-0" />
                  <span className="flex-1 text-left">{n.group}</span>
                  {!openGroups[n.group] && badge(n.testid)}
                  <ChevronDown className={`h-4 w-4 transition-transform ${openGroups[n.group] ? "rotate-180" : ""}`} />
                </button>
                {openGroups[n.group] && (
                  <div className="mt-1 space-y-1 pl-4 border-l border-white/10 ml-4">
                    {n.children.map((c) => (
                      <NavLink key={c.to} to={c.to} data-testid={c.testid} end
                        className={`sidebar-link flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium ${
                          childActive(c) ? "bg-white text-brand-deep shadow-sm" : "text-slate-300 hover:bg-white/10 hover:text-white"
                        }`}>
                        <c.icon className="h-4 w-4 shrink-0" />
                        {c.label}
                        {badge(c.testid)}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ) : n.external ? (
              <a key={n.external} href={n.external} data-testid={n.testid} className={linkClass({ isActive: false })}
                onClick={(e) => { if (!window.confirm(`Leave Operations Hub And Open ${n.label}?`)) e.preventDefault(); }}>
                <n.icon className="h-5 w-5 shrink-0" />
                {n.label}
              </a>
            ) : (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid} className={linkClass}>
                <n.icon className="h-5 w-5 shrink-0" />
                {n.label}
                {n.testid === "nav-opportunities" && urgent > 0 && <span className="ml-auto rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white" data-testid="nav-opportunities-urgent-badge">{urgent}</span>}
              </NavLink>
            )
          ))}
        </nav>
        <div className="border-t border-white/10 p-3">
          <div className="px-2 pb-2 text-center text-xs font-medium tracking-wide text-white">At the Core of Global Resources.</div>
          <button type="button" onClick={() => setProfileOpen(true)} className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-white/10" data-testid="current-user-email">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/15 text-sm font-semibold text-white">
              {user?.avatar ? <img src={user.avatar} alt="" className="h-full w-full object-cover" /> : (user?.name || user?.email || "?").trim().charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-white">{user?.name || "User"}</div>
              <div className="truncate text-xs text-slate-400">{user?.job_title || user?.role}</div>
            </div>
            <Pencil className="h-3.5 w-3.5 text-slate-400" />
          </button>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="sidebar-link mt-1.5 flex w-full items-center justify-center gap-2 rounded-lg border border-white/15 px-3 py-2.5 text-sm font-medium text-slate-200 hover:bg-white/10 hover:text-white"
          >
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
          <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 lg:ml-64">
        {isImpersonating && (
          <div className="flex items-center justify-between gap-4 bg-amber-100 px-8 py-2 text-sm text-amber-900 border-b border-amber-200" data-testid="impersonation-banner">
            <span className="flex items-center gap-2">
              <Eye className="h-4 w-4" /> Viewing as <strong>{user?.name || user?.email}</strong>
              {user?.impersonator_name ? ` — signed in by ${user.impersonator_name}` : ""}
            </span>
            <button onClick={returnToAdmin} className="font-semibold underline hover:no-underline" data-testid="return-to-admin">
              Return to my account
            </button>
          </div>
        )}
        <header className="sticky top-0 z-20 flex min-h-16 flex-wrap items-center justify-between gap-x-2 gap-y-2 border-b border-border bg-white/90 px-4 py-2 backdrop-blur sm:h-16 sm:flex-nowrap sm:gap-2 sm:px-8 sm:py-0">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <button type="button" onClick={() => setMobileNav(true)} className="-ml-1 shrink-0 rounded-md p-2 text-slate-600 hover:bg-slate-100 lg:hidden" data-testid="sidebar-open-button" aria-label="Open menu">
              <Menu className="h-5 w-5" />
            </button>
            <h1 className="truncate font-heading text-xl font-bold tracking-tight text-slate-900" data-testid="page-title">
              {title}
            </h1>
          </div>
          {(actions || !noRefresh) && (
            <div className="flex w-full shrink-0 flex-wrap items-center justify-end gap-2 sm:w-auto sm:max-w-none sm:flex-nowrap sm:overflow-x-auto">
              {!noRefresh && (
                <Button variant="outline" onClick={doRefresh} title="Reload the latest details saved by the team (unsaved edits on this page are discarded)" data-testid="page-refresh-btn">
                  <RefreshCw className={`mr-2 h-4 w-4 ${spin ? "animate-spin" : ""}`} />Refresh
                </Button>
              )}
              {actions}
            </div>
          )}
        </header>
        <main className="flex-1 overflow-x-hidden p-4 sm:p-8"><div key={title} className="min-w-0 animate-fade-up">{children}</div></main>
      </div>
    </div>
  );
}
